import {
	MaterialSymbol,
	type MaterialSymbolRelativeSize,
} from "./MaterialSymbol";
import "./Button.css";
import { smartenText } from "@utils/text";
import { type JSX, type ParentComponent, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import type { TextColor } from "./typography/Text";

function hasVisibleChildContent(children: JSX.Element): boolean {
	if (children == null || children === false) {
		return false;
	}
	if (typeof children === "string") {
		return children.trim().length > 0;
	}
	if (Array.isArray(children)) {
		return children.some((child) => hasVisibleChildContent(child));
	}
	return true;
}

export type ButtonVariant =
	| "primary"
	| "secondary"
	| "tertiary"
	| "text"
	| "ghost"
	| "link";

export type ButtonTheme =
	| "blue"
	| "monochrome"
	| "black"
	| "purple"
	| "orange"
	| "success"
	| "danger"
	| "white"
	| "grey"
	| "grey600"
	| "mint-grad";

export type Props = Omit<
	JSX.ButtonHTMLAttributes<HTMLButtonElement>,
	"type" | "style"
> & {
	as?: "button" | "a";
	href?: string;
	target?: HTMLAnchorElement["target"];
	rel?: string;
	/** Id of an element describing the control, for when a custom tooltip replaces `title`. */
	ariaDescribedBy?: string;
	type?: "button" | "submit" | "reset";
	variant?: ButtonVariant;
	theme?: ButtonTheme;
	warning?: boolean;
	monospaced?: boolean;
	symbol?: string | [string, string];
	/**
	 * Class for the symbol itself, e.g. `animate-spin`. Use this rather than a
	 * child for a state the symbol expresses — a child, even a conditional one
	 * that renders nothing, stops the button counting as icon-only and so drops
	 * the square and circle sizing.
	 */
	symbolClass?: string;
	symbolColor?: TextColor;
	symbolFilled?: boolean;
	symbolRelativeSize?: MaterialSymbolRelativeSize | number;
	symbolPosition?: "before" | "after";
	size?: "compact" | "small" | "medium" | "large";
	shape?: "square" | "circle";
	layout?: "default" | "icon";
	style?: JSX.CSSProperties | string;
	touchTarget?: "standard" | "compact";
	fullWidth?: boolean;
	smarten?: boolean;
	/** remove padding from the start or end of the button */
	noPadding?: "start" | "end";
};

/**
 * Interactive buttons for triggering actions with variants, themes, and icon support.
 *
 * [Design System →](http://localhost:5174/component/Button)
 */
export const Button: ParentComponent<Props> = (props) => {
	const [local, rest] = splitProps(props, [
		"as",
		"href",
		"target",
		"rel",
		"ariaDescribedBy",
		"disabled",
		"type",
		"noPadding",
		"size",
		"class",
		"variant",
		"symbol",
		"symbolClass",
		"symbolColor",
		"symbolFilled",
		"symbolRelativeSize",
		"theme",
		"warning",
		"symbolPosition",
		"monospaced",
		"children",
		"shape",
		"layout",
		"touchTarget",
		"classList",
		"fullWidth",
		"smarten",
	]);
	const isLink = () => local.as === "a";
	const symbolPosition = () => local.symbolPosition ?? "before";
	const hasGraphic = () => local.symbol !== undefined;
	const isIconOnly = () =>
		hasGraphic() && !hasVisibleChildContent(local.children);

	const renderSymbol = (symbol: string) => (
		<MaterialSymbol
			symbol={symbol}
			class={local.symbolClass}
			relativeSize={local.symbolRelativeSize ?? 1.14}
			color={local.symbolColor}
			filled={local.symbolFilled}
		/>
	);

	const graphicAt = (position: "before" | "after") => {
		const symbol = local.symbol;
		if (symbol === undefined) {
			return null;
		}
		if (Array.isArray(symbol)) {
			return renderSymbol(position === "before" ? symbol[0] : symbol[1]);
		}
		return symbolPosition() === position ? renderSymbol(symbol) : null;
	};

	const label = () =>
		local.smarten === true && typeof local.children === "string"
			? smartenText(local.children)
			: local.children;

	const renderedLabel = () =>
		isLink() && hasVisibleChildContent(local.children) ? (
			<span class="gvtui-button-link__content">{label()}</span>
		) : (
			label()
		);

	return (
		<Dynamic
			component={isLink() ? "a" : "button"}
			class="gvtui-button"
			classList={{
				[`gvtui-button--${local.size}`]: Boolean(local.size),
				[`${local.class}`]: Boolean(local.class),
				[`${local.variant}`]: Boolean(local.variant),
				[`${local.theme}`]: Boolean(local.theme),
				"gvtui-button--warning": Boolean(local.warning),
				[`gvtui-button--shape-${local.shape}`]: Boolean(local.shape),
				"gvtui-button--icon-only": isIconOnly(),
				"gvtui-button--layout-icon": local.layout === "icon",
				"gvtui-button--touch-target": local.touchTarget === "standard",
				"gvtui-button--touch-target-compact": local.touchTarget === "compact",
				"gvtui-button--full-width": local.fullWidth,
				"gvtui-button--monospaced": Boolean(local.monospaced),
				"gvtui-button-link": isLink(),
				"gvtui-button--no-padding-start": local.noPadding === "start",
				"gvtui-button--no-padding-end": local.noPadding === "end",
				disabled: isLink() && Boolean(local.disabled),
				...(local.classList ?? {}),
			}}
			href={isLink() && !local.disabled ? local.href : undefined}
			target={isLink() ? (local.target ?? "_self") : undefined}
			rel={isLink() ? local.rel : undefined}
			aria-describedby={local.ariaDescribedBy}
			aria-disabled={isLink() && local.disabled ? true : undefined}
			tabIndex={isLink() && local.disabled ? -1 : undefined}
			type={isLink() ? undefined : local.type}
			disabled={isLink() ? undefined : local.disabled}
			{...rest}
		>
			<div>
				{graphicAt("before")}
				{renderedLabel()}
				{graphicAt("after")}
			</div>
		</Dynamic>
	);
};

/**
 * Monochrome-themed secondary button variant.
 *
 * [Design System →](http://localhost:5174/component/Button)
 */
export const ButtonMonochrome: ParentComponent<Props> = (props) => {
	return (
		<Button variant="secondary" theme="monochrome" {...props}>
			{props.children}
		</Button>
	);
};
